import React, { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import html2canvas from "html2canvas";
import { Download, Printer, QrCode, ShieldCheck } from "lucide-react";
import { STATUS_META } from "@/components/DashboardShell";

export default function RegistrationCard({ cpmId, name, category, status }) {
  const canvasWrapRef = useRef(null);
  const cardRef = useRef(null);

  if (!cpmId) {
    return (
      <div
        className="rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-[0_4px_20px_-2px_rgba(39,174,96,0.05)]"
        data-testid="registration-card-empty"
      >
        <QrCode className="mx-auto h-10 w-10 text-gray-300" />
        <p className="mt-3 text-sm text-[#6B7280]">
          Kartu Bukti Pendaftaran akan tersedia setelah ID Peserta (CPM-ID) Anda terbit.
        </p>
      </div>
    );
  }

  const statusLabel = STATUS_META[status || "draft"]?.label || "Draft";

  const downloadQr = async () => {
    const node = cardRef.current;
    if (!node) return;
    const canvas = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
    const link = document.createElement("a");
    link.download = `Kartu-Peserta-${String(cpmId).replace(/[^A-Za-z0-9]/g, "-")}.png`;
    link.href = canvas.toDataURL("image/png");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const printCard = () => {
    const canvas = canvasWrapRef.current?.querySelector("canvas");
    const qrData = canvas ? canvas.toDataURL("image/png") : "";
    const win = window.open("", "_blank", "width=720,height=900");
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html lang="id"><head><meta charset="utf-8" />
      <title>Kartu Bukti Pendaftaran - ${cpmId}</title>
      <style>
        *{box-sizing:border-box;font-family:Arial,Helvetica,sans-serif;margin:0;}
        body{padding:24px;color:#1F2937;}
        .card{max-width:380px;margin:0 auto;border:2px solid #0B6B3A;border-radius:16px;overflow:hidden;}
        .head{background:#0B6B3A;color:#fff;padding:18px 20px;display:flex;align-items:center;gap:12px;}
        .logo{width:42px;height:42px;border-radius:10px;background:#27AE60;display:flex;align-items:center;justify-content:center;font-weight:800;}
        .head small{display:block;opacity:.85;font-size:10px;}
        .head strong{font-size:15px;}
        .body{padding:24px;text-align:center;}
        .qr img{width:180px;height:180px;}
        .qr div{font-size:10px;color:#6B7280;margin-top:6px;}
        .info{margin-top:20px;}
        .info .row{margin:0 0 14px;}
        .info span{display:block;color:#6B7280;font-size:10px;text-transform:uppercase;letter-spacing:.06em;}
        .info b{font-size:15px;}
        .pair{display:flex;justify-content:center;gap:40px;}
        .note{background:#F0FBF5;border-top:1px solid #D3EFDF;padding:14px 20px;font-size:11px;color:#0B6B3A;text-align:center;}
      </style></head><body>
      <div class="card">
        <div class="head"><div class="logo">MDJ</div><div><strong>Kartu Bukti Pendaftaran</strong><small>Masa Depan Jakarta Scholarship • BAZNAS (BAZIS) DKI Jakarta</small></div></div>
        <div class="body">
          <div class="qr"><img src="${qrData}" alt="QR Code" /><div>Pindai saat wawancara</div></div>
          <div class="info">
            <div class="row"><span>Nama Peserta</span><b>${name || "-"}</b></div>
            <div class="row"><span>ID Peserta (CPM-ID)</span><b>${cpmId}</b></div>
            <div class="pair">
              <div class="row"><span>Kategori</span><b>${category || "-"}</b></div>
              <div class="row"><span>Status</span><b>${statusLabel}</b></div>
            </div>
          </div>
        </div>
        <div class="note">Tunjukkan QR Code ini kepada Pewawancara saat sesi wawancara untuk membuka formulir penilaian Anda.</div>
      </div>
      <script>window.onload=function(){setTimeout(function(){window.print();},300);}</script>
      </body></html>`);
    win.document.close();
  };

  return (
    <div className="mx-auto max-w-[420px] space-y-5" data-testid="registration-card">
      <div ref={cardRef} className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-[0_4px_20px_-2px_rgba(39,174,96,0.08)]">
        <div className="flex items-center gap-3 bg-gradient-to-r from-[#0B6B3A] to-[#27AE60] px-5 py-5 text-white">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 font-display text-sm font-extrabold">
            MDJ
          </span>
          <div className="min-w-0">
            <strong className="block font-display text-base font-extrabold leading-tight">
              Kartu Bukti Pendaftaran
            </strong>
            <span className="block text-[11px] leading-tight text-white/85">
              Masa Depan Jakarta Scholarship • BAZNAS (BAZIS) DKI Jakarta
            </span>
          </div>
        </div>

        <div className="flex flex-col items-center gap-5 p-6">
          <div ref={canvasWrapRef} className="rounded-xl border-2 border-[#E8F6EE] bg-white p-3 text-center">
            <QRCodeCanvas value={String(cpmId)} size={180} level="M" includeMargin data-testid="registration-card-qr" />
            <p className="mt-1 text-[10px] font-semibold text-[#6B7280]">Pindai saat wawancara</p>
          </div>

          <dl className="grid w-full gap-4 text-center text-sm">
            <div>
              <dt className="text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">Nama Peserta</dt>
              <dd className="mt-0.5 font-bold text-[#1F2937]" data-testid="registration-card-name">{name || "-"}</dd>
            </div>
            <div>
              <dt className="text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">ID Peserta (CPM-ID)</dt>
              <dd className="mt-0.5 font-mono font-bold text-[#0B6B3A]" data-testid="registration-card-cpm">{cpmId}</dd>
            </div>
            <div className="flex justify-center gap-10">
              <div>
                <dt className="text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">Kategori</dt>
                <dd className="mt-0.5 font-semibold text-[#1F2937]">{category || "-"}</dd>
              </div>
              <div>
                <dt className="text-[11px] font-bold uppercase tracking-wide text-[#6B7280]">Status</dt>
                <dd className="mt-0.5 font-semibold text-[#1F2937]">{statusLabel}</dd>
              </div>
            </div>
          </dl>
        </div>

        <div className="flex items-start gap-2 border-t border-[#D3EFDF] bg-[#F0FBF5] px-5 py-4">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#0B6B3A]" />
          <p className="text-xs font-semibold text-[#0B6B3A]">
            Tunjukkan QR Code ini kepada Pewawancara saat sesi wawancara untuk membuka formulir penilaian Anda.
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row">
        <button
          type="button"
          onClick={downloadQr}
          data-testid="download-qr-btn"
          className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#27AE60] px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-[#0B6B3A]"
        >
          <Download className="h-4 w-4" /> Unduh Kartu
        </button>
        <button
          type="button"
          onClick={printCard}
          data-testid="print-card-btn"
          className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-300 px-5 py-2.5 text-sm font-bold text-[#1F2937] transition-colors hover:bg-gray-50"
        >
          <Printer className="h-4 w-4" /> Cetak / Simpan PDF
        </button>
      </div>
    </div>
  );
}
